import { CAPACIDADE_POR_HORARIO, ESPACAMENTO_MINIMO_MINUTOS, JANELA_AGENDAMENTO } from '@/data/config';
import { somarMinutos } from '@/lib/format';
import { trackRequest } from '@/lib/request-progress';
import type {
  Agendamento,
  EventoHistorico,
  HorarioDisponivel,
  Instalacao,
  InstalacaoBusca,
  NovidadeInput,
  NovoAgendamento,
  Novidade,
  PaginaInstalacoes,
  PanDataSource,
  RetencaoHorario,
  VersaoDisponivel,
  VersoesInstalacao,
} from '@/data/types';
import { rotuloHorario, semAtualizacoesDisponiveis } from '../rest/normalize';
import * as fixtures from './fixtures';

/**
 * Fonte de dados de demonstração.
 *
 * Existe por um motivo que o app do monorepo resolvia com `json-server` + um
 * `db.json` fora do build: aqui a aplicação precisa subir sozinha, sem Keycloak,
 * sem ERPrompt-API e sem processo auxiliar. É a mesma interface `PanDataSource`
 * da fonte REST, então nenhuma tela sabe qual das duas está montada.
 *
 * As regras de negócio (capacidade por horário, janela 07:00–18:00, versões por
 * instalação, cancelamento como transição de estado) são aplicadas aqui de
 * verdade — não é um eco de fixtures.
 */

const LATENCIA_MS = 320;

let agendamentos: Agendamento[] = [...fixtures.agendamentos];
let historico: EventoHistorico[] = [...fixtures.historico];
let novidades: Novidade[] = [...fixtures.novidades];
let proximoId = 30018;
let proximoHoldId = 1;
let proximoIdNovidade = 1;

/** Sem identidade real no modo demo (ver `src/main.tsx`) — quem "sou eu" pra
 * `listMeusAgendamentos` é simulado por este conjunto de ids: os criados
 * diretamente (não em lote) nos fixtures — incluindo os já finalizados
 * (Concluído/Falha/Falha Parcial) —, mais qualquer um que a própria sessão
 * de demonstração criar depois (ver `criarAgendamento`). */
let idsMeusAgendamentos = new Set<string>(['sch-30011', 'sch-30014', 'sch-30015', 'sch-30016', 'sch-30017']);

const RETENCAO_TTL_SEGUNDOS = 120;

interface Retencao {
  chaveSlot: string;
  instalacoes: string[];
  timer: ReturnType<typeof setTimeout>;
}

/** Retenções ativas em memória — mesma ideia do `hold:<slot>:<id>`/
 * `hold:instalacao:<id>` do backend real, só que sem Redis: um `Map` com
 * `setTimeout` cumprindo o papel do TTL. */
const retencoes = new Map<string, Retencao>();

function chaveSlot(data: string, horario: string): string {
  return `${data}_${horario}`;
}

/** Vagas seguradas por retenções ainda ativas nesse horário. */
function ocupacaoRetida(data: string, horario: string): number {
  const chave = chaveSlot(data, horario);
  let total = 0;
  for (const retencao of retencoes.values()) {
    if (retencao.chaveSlot === chave) total += retencao.instalacoes.length;
  }
  return total;
}

/** Dessas instalações, quais já têm um agendamento 'Programado' ou 'Executando' vivo. */
function instalacoesJaAgendadas(instalacoesAlvo: string[]): string[] {
  const agendadas = new Set(
    agendamentos
      .filter((item) => item.status === 'Programado' || item.status === 'Executando')
      .map((item) => item.instalacaoId),
  );
  return instalacoesAlvo.filter((id) => agendadas.has(id));
}

/** Dessas instalações, quais já estão seguradas por OUTRA retenção viva. */
function instalacoesTravadas(instalacoesAlvo: string[], holdIdAtual?: string): string[] {
  const travadas = new Set<string>();
  for (const [id, retencao] of retencoes) {
    if (id === holdIdAtual) continue;
    for (const instalacao of retencao.instalacoes) {
      if (instalacoesAlvo.includes(instalacao)) travadas.add(instalacao);
    }
  }
  return [...travadas];
}

function espera<T>(valor: T): Promise<T> {
  return trackRequest(
    () =>
      new Promise<T>((resolve) => {
        setTimeout(() => resolve(valor), LATENCIA_MS);
      }),
  );
}

function horariosDaJanela(): string[] {
  const inicio = Number(JANELA_AGENDAMENTO.inicio.slice(0, 2));
  const fim = Number(JANELA_AGENDAMENTO.fim.slice(0, 2));
  return Array.from({ length: fim - inicio + 1 }, (_, i) => `${String(inicio + i).padStart(2, '0')}:00`);
}

/** Quantas instalações já ocupam um horário numa data (só agendamentos vivos). */
function ocupacao(data: string, horario: string): number {
  return agendamentos.filter((item) => item.data === data && item.hora === horario && item.status === 'Programado').length;
}

function nomeDaInstalacao(id: string): string {
  return fixtures.instalacoes.find((instalacao) => instalacao.id === id)?.nome ?? id;
}

/** Espelha `STATUS_ATIVOS_HUB` do backend (`AgendamentoService`) — cancelado
 * ou já finalizado (Concluído/Falha/Falha Parcial) só continua visível no
 * Histórico, nunca no hub de agendamentos ativos (`listAgendamentos`).
 * `listMeusAgendamentos` ignora esta restrição de propósito: lá o objetivo é
 * ver tudo que a pessoa agendou, inclusive o que já terminou. */
const STATUS_ATIVOS_HUB = new Set(['Programado', 'Executando']);

export const demoDataSource: PanDataSource = {
  kind: 'demo',

  listInstalacoes: () => espera<Instalacao[]>(fixtures.instalacoes.map((item) => ({ ...item }))),

  listInstalacoesPaginado: (filtro) => {
    const termo = filtro.busca?.trim().toLowerCase() ?? '';
    const temAna = fixtures.instalacoes.some((item) => item.ana);
    const temInterno = fixtures.instalacoes.some((item) => item.interno);
    const filtradas = fixtures.instalacoes.filter((item) => {
      if (filtro.ana !== undefined && item.ana !== filtro.ana) return false;
      if (filtro.interno !== undefined && item.interno !== filtro.interno) return false;
      if (!termo) return true;
      return `${item.nome} ${item.codigo ?? ''} ${item.cliente?.nome ?? ''} ${item.regiao ?? ''}`
        .toLowerCase()
        .includes(termo);
    });
    return espera<PaginaInstalacoes>({
      itens: filtradas.slice(filtro.offset, filtro.offset + filtro.limit).map((item) => ({ ...item })),
      total: filtradas.length,
      temAna,
      temInterno,
    });
  },

  listAgendamentos: () =>
    espera<Agendamento[]>(
      agendamentos.filter((item) => STATUS_ATIVOS_HUB.has(item.status)).map((item) => ({ ...item })),
    ),

  listMeusAgendamentos: () =>
    espera<Agendamento[]>(agendamentos.filter((item) => idsMeusAgendamentos.has(item.id)).map((item) => ({ ...item }))),

  listHistorico: () =>
    espera<EventoHistorico[]>(
      [...historico].sort((a, b) => (b.data ?? '').localeCompare(a.data ?? '')).map((item) => ({ ...item })),
    ),

  listNovidades: () => espera<Novidade[]>(novidades.map((item) => ({ ...item }))),

  async criarNovidade(input: NovidadeInput) {
    const nova: Novidade = { ...input, id: `demo-novidade-${proximoIdNovidade++}` };
    novidades = [nova, ...novidades];
    return espera(nova);
  },

  async excluirNovidade(id: string) {
    novidades = novidades.filter((item) => item.id !== id);
    return espera(undefined);
  },

  listVersoes: (instalacaoId) => {
    const catalogo = fixtures.versoesPorInstalacao[instalacaoId] ?? [];
    // O fixture não modela ramo/LD-QA-Master (é anterior a esse conceito) —
    // aqui tudo entra numa guia única, só pra manter o modo demo funcionando.
    const versoes: VersaoDisponivel[] =
      catalogo.length === 0
        ? [semAtualizacoesDisponiveis()]
        : catalogo.map((item) => ({ ...item, semAtualizacoesDisponiveis: false }));
    return espera<VersoesInstalacao>({
      versaoAtualCliente: '',
      estagioAtual: 'atual',
      guias: [{ chave: 'atual', rotulo: 'Atual', ramo: '', versoes }],
    });
  },

  listDisponibilidade: (data) =>
    espera<HorarioDisponivel[]>(
      horariosDaJanela().map((horario) => {
        const disponivel = Math.max(
          0,
          CAPACIDADE_POR_HORARIO - ocupacao(data, horario) - ocupacaoRetida(data, horario),
        );
        return {
          id: `${data}_${horario}`,
          horario,
          data,
          disponivel,
          total: CAPACIDADE_POR_HORARIO,
          nome: rotuloHorario(horario, disponivel, CAPACIDADE_POR_HORARIO),
        };
      }),
    ),

  async reterHorario(input) {
    const instalacoes = [...new Set(input.instalacoesAlvo)];

    const agendadas = instalacoesJaAgendadas(instalacoes);
    if (agendadas.length > 0) {
      await espera(undefined);
      throw new Error(
        `As seguintes instalações já têm uma atualização programada: ${agendadas.join(', ')}. Cancele antes de agendar outra.`,
      );
    }

    const travadas = instalacoesTravadas(instalacoes);
    if (travadas.length > 0) {
      await espera(undefined);
      throw new Error(
        `As seguintes instalações já estão sendo agendadas por outra pessoa agora: ${travadas.join(', ')}. Tente novamente em instantes.`,
      );
    }

    // Quantas já ocupam esse horário ANTES desta retenção — vira a posição
    // inicial (0-based) das instalações desta retenção na fila do horário,
    // pra estimar o horário real de disparo (espelha `criar_retencao` do
    // backend). É só uma estimativa: outra retenção/confirmação concorrente
    // pode mudar a posição de verdade até a confirmação.
    const posicaoInicial = ocupacao(input.data, input.hora) + ocupacaoRetida(input.data, input.hora);
    const disponivel = CAPACIDADE_POR_HORARIO - posicaoInicial;
    if (instalacoes.length > disponivel) {
      await espera(undefined);
      throw new Error('Capacidade insuficiente para o horário escolhido.');
    }

    const holdId = `hold-${proximoHoldId++}`;
    const timer = setTimeout(() => retencoes.delete(holdId), RETENCAO_TTL_SEGUNDOS * 1000);
    retencoes.set(holdId, { chaveSlot: chaveSlot(input.data, input.hora), instalacoes, timer });

    return espera<RetencaoHorario>({
      holdId,
      ttlSegundos: RETENCAO_TTL_SEGUNDOS,
      expiraEm: new Date(Date.now() + RETENCAO_TTL_SEGUNDOS * 1000).toISOString(),
      horarioRealInicio: somarMinutos(input.hora, posicaoInicial * ESPACAMENTO_MINIMO_MINUTOS),
      horarioRealFim: somarMinutos(
        input.hora,
        (posicaoInicial + instalacoes.length - 1) * ESPACAMENTO_MINIMO_MINUTOS,
      ),
    });
  },

  async liberarRetencao(holdId) {
    const retencao = retencoes.get(holdId);
    if (retencao) {
      clearTimeout(retencao.timer);
      retencoes.delete(holdId);
    }
    await espera(undefined);
  },

  async criarAgendamento(input: NovoAgendamento) {
    if (input.holdId) {
      const retencao = retencoes.get(input.holdId);
      if (retencao) {
        clearTimeout(retencao.timer);
        retencoes.delete(input.holdId);
      }
    }

    const agendadas = instalacoesJaAgendadas(input.instalacoesAlvo);
    if (agendadas.length > 0) {
      throw new Error(
        `As seguintes instalações já têm uma atualização programada: ${agendadas.join(', ')}. Cancele antes de agendar outra.`,
      );
    }

    const vagas = CAPACIDADE_POR_HORARIO - ocupacao(input.data, input.hora) - ocupacaoRetida(input.data, input.hora);
    if (input.instalacoesAlvo.length > vagas) {
      throw new Error(
        `O horário ${input.hora} de ${input.data} tem ${vagas} vaga(s) e você selecionou ${input.instalacoesAlvo.length} instalação(ões).`,
      );
    }

    const emLote = input.instalacoesAlvo.length > 1;
    const batchId = emLote ? `lote-${Math.floor(Math.random() * 9000 + 1000)}` : null;
    const criadoEm = new Date().toISOString();

    const novos = input.instalacoesAlvo.map<Agendamento>((instalacaoId) => ({
      id: `sch-${proximoId++}`,
      instalacaoId,
      instalacao: nomeDaInstalacao(instalacaoId),
      versao: input.versao,
      data: input.data,
      hora: input.hora,
      status: 'Programado',
      origem: emLote ? 'Lote' : 'Direto',
      batchId,
      createdAt: criadoEm,
    }));

    // Quem cria um agendamento no modo demo é sempre "a pessoa da sessão" —
    // por isso todo novo agendamento entra direto em `idsMeusAgendamentos`.
    for (const item of novos) idsMeusAgendamentos.add(item.id);

    agendamentos = [...novos, ...agendamentos];
    historico = [
      ...novos.map<EventoHistorico>((item) => ({
        id: `upd-${item.id}`,
        instalacao: item.instalacao,
        versao: item.versao,
        data: `${item.data}T${item.hora}:00`,
        status: 'Programado',
        origem: item.origem,
        batchId: item.batchId,
      })),
      ...historico,
    ];

    await espera(undefined);
  },

  async cancelarAgendamento(id) {
    const alvo = agendamentos.find((item) => item.id === id);
    if (!alvo) throw new Error(`O agendamento ${id} não existe mais nesta lista.`);
    if (alvo.status === 'Cancelado') throw new Error(`O agendamento ${id} já estava cancelado.`);

    agendamentos = agendamentos.map((item) => (item.id === id ? { ...item, status: 'Cancelado' } : item));
    historico = historico.map((item) =>
      item.id === `upd-${id}` ? { ...item, status: 'Cancelado' } : item,
    );

    await espera(undefined);
  },

  async dispararAgendamentoAgora(id) {
    // Sem Celery/FastU real no modo demo — não há o que efetivamente
    // disparar. Só valida que o agendamento existe e está Programado, igual
    // o backend faria antes de enfileirar.
    const alvo = agendamentos.find((item) => item.id === id);
    if (!alvo) throw new Error(`O agendamento ${id} não existe mais nesta lista.`);
    if (alvo.status !== 'Programado') {
      throw new Error(`O agendamento ${id} não está mais Programado.`);
    }
    await espera(undefined);
  },

  buscarInstalacoesParaPersonificar(query) {
    const termo = query.trim().toLowerCase();
    const encontradas = termo
      ? fixtures.instalacoes.filter((item) =>
          `${item.nome} ${item.codigo ?? ''} ${item.id}`.toLowerCase().includes(termo),
        )
      : fixtures.instalacoes;
    return espera<InstalacaoBusca[]>(
      encontradas
        .slice(0, 20)
        .map((item) => ({ id: item.id, nome: item.nome, codigo: item.codigo, instalacaoInfraId: null, ana: item.ana, interno: item.interno })),
    );
  },

  async registrarPersonificacao() {
    // Sem Keycloak/backend real no modo demo — não há auditoria pra registrar.
    await espera(undefined);
  },

  async abrirOuRenovarSessaoPersonificacao() {
    // Sem concorrência real pra simular no modo demo — sempre concede.
    await espera(undefined);
  },

  async encerrarSessaoPersonificacao() {
    await espera(undefined);
  },
};

/** Só para testes: devolve a demo ao estado inicial. */
export function resetDemoDataSource(): void {
  agendamentos = [...fixtures.agendamentos];
  historico = [...fixtures.historico];
  novidades = [...fixtures.novidades];
  proximoId = 30018;
  proximoHoldId = 1;
  proximoIdNovidade = 1;
  idsMeusAgendamentos = new Set(['sch-30011', 'sch-30014', 'sch-30015', 'sch-30016', 'sch-30017']);
  for (const retencao of retencoes.values()) clearTimeout(retencao.timer);
  retencoes.clear();
}
